import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  formatCandidateStatus,
  formatDateTime,
  getEvaluationRoundLabel,
} from "@/utils/formatters";
import { getCandidateStatusColor } from "@/utils/statusColors";
import { History, User, MessageSquare, Trophy, FileText } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getSafeErrorMessage } from "@/utils/safeErrorMessage";

interface CandidateEvaluationHistoryProps {
  candidateId: string;
  refreshKey: number;
  onReevaluationComplete?: () => void;
}

export function CandidateEvaluationHistory({
  candidateId,
  refreshKey,
  onReevaluationComplete,
}: CandidateEvaluationHistoryProps) {
  type ReevaluationTarget = "cv" | "assessment";

  const [openId, setOpenId] = useState<string | null>(null);
  const [reevaluationTarget, setReevaluationTarget] =
    useState<ReevaluationTarget | null>(null);
  const formatLabel = (label: string) =>
    label.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const renderValue = (value: any): JSX.Element | string => {
    if (value === null || value === undefined) {
      return <span>N/A</span>;
    }
    if (Array.isArray(value)) {
      return (
        <div className="ml-4 space-y-1">
          {value.map((item, idx) => (
            <div key={idx}>{renderValue(item)}</div>
          ))}
        </div>
      );
    }
    if (typeof value === "object") {
      return (
        <div className="ml-4 space-y-1">
          {Object.entries(value).map(([k, v]) => (
            <div key={k}>
              <span className="font-medium">{formatLabel(k)}:</span>
              {typeof v === "object" ? (
                <div>{renderValue(v)}</div>
              ) : (
                <span> {String(v)}</span>
              )}
            </div>
          ))}
        </div>
      );
    }
    return <span>{String(value)}</span>;
  };
  const { toast } = useToast();
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [isReevaluatingCv, setIsReevaluatingCv] = useState(false);
  const [sendCvFailureEmail, setSendCvFailureEmail] = useState(false);

  const { data: evaluations = [], isLoading } = useQuery({
    queryKey: ["evaluations", candidateId, refreshKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("evaluation_rounds")
        .select(
          `
          *,
          profiles:evaluator_id(full_name)
        `
        )
        .eq("candidate_id", candidateId)
        .order("evaluated_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Evaluation History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground">
            Loading evaluation history...
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleReevaluate = async () => {
    setIsReevaluating(true);
    try {
      await invokeEdgeFunction("evaluate-assessment", {
        body: { candidateId },
      });
      toast({
        title: "Reevaluation Started",
        description: "The assessment is being re-evaluated.",
      });
      onReevaluationComplete?.();
    } catch (err) {
      console.error("Reevaluation error:", err);
      toast({
        title: "Error",
        description: getSafeErrorMessage(
          err,
          "Failed to trigger reevaluation."
        ),
        variant: "destructive",
      });
    } finally {
      setIsReevaluating(false);
    }
  };

  const handleCvReevaluate = async (sendEmail: boolean) => {
    setIsReevaluatingCv(true);
    try {
      await invokeEdgeFunction("evaluate-cv-gemini", {
        body: { candidateId, sendFailureEmail: sendEmail },
      });
      toast({
        title: "CV Reevaluation Started",
        description: "The CV profile is being re-evaluated.",
      });
      onReevaluationComplete?.();
    } catch (err) {
      console.error("CV reevaluation error:", err);
      toast({
        title: "Error",
        description: getSafeErrorMessage(
          err,
          "Failed to trigger CV reevaluation."
        ),
        variant: "destructive",
      });
    } finally {
      setIsReevaluatingCv(false);
    }
  };

  const handleConfirmReevaluation = async () => {
    const target = reevaluationTarget;
    const sendEmail = sendCvFailureEmail;
    setReevaluationTarget(null);
    setSendCvFailureEmail(false);

    if (target === "cv") {
      await handleCvReevaluate(sendEmail);
      return;
    }

    if (target === "assessment") {
      await handleReevaluate();
    }
  };

  const reevaluationConfig =
    reevaluationTarget === "cv"
      ? {
          title: "Reevaluate CV?",
          description:
            "This will start a fresh AI reevaluation of the candidate's CV profile. The previous CV evaluation result will be removed and replaced.",
          actionLabel: "Reevaluate CV",
          isPending: isReevaluatingCv,
        }
      : {
          title: "Reevaluate assessment?",
          description:
            "This will start a fresh AI reevaluation of the candidate's assessment results.",
          actionLabel: "Reevaluate assessment",
          isPending: isReevaluating,
        };

  return (
    <Card>
      <CardHeader className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <History className="h-5 w-5" />
          Evaluation History
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSendCvFailureEmail(false);
              setReevaluationTarget("cv");
            }}
            disabled={isReevaluatingCv}
          >
            {isReevaluatingCv ? "Reevaluating CV..." : "Reevaluate CV"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setReevaluationTarget("assessment")}
            disabled={isReevaluating}
          >
            {isReevaluating ? "Reevaluating..." : "Reevaluate Assessment"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {evaluations.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            No evaluations recorded yet.
          </div>
        ) : (
          <div className="space-y-4">
            {evaluations.map((evaluation) => (
              <div key={evaluation.id} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">
                      {getEvaluationRoundLabel(evaluation.round_name as any)}
                    </span>
                    {evaluation.status && (
                      <Badge className={getCandidateStatusColor(evaluation.status)}>
                        {formatCandidateStatus(evaluation.status)}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {formatDateTime(evaluation.evaluated_at)}
                  </div>
                </div>

                <div className="space-y-2">
                  {evaluation.score && (
                    <div className="flex items-center gap-2">
                      <Trophy className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        Score: {evaluation.score}%
                      </span>
                    </div>
                  )}

                  {evaluation.profiles && (
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        Evaluated by: {evaluation.profiles.full_name}
                      </span>
                    </div>
                  )}

                  {evaluation.feedback && (
                    <div className="flex items-start gap-2">
                      <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5" />
                      <div className="text-sm">
                        <div className="font-medium mb-1">Feedback:</div>
                        <div className="text-muted-foreground whitespace-pre-line">
                          {evaluation.feedback}
                        </div>
                      </div>
                    </div>
                  )}

                  {evaluation.evaluation_data && (
                    <Dialog
                      open={openId === evaluation.id}
                      onOpenChange={(open) => setOpenId(open ? evaluation.id : null)}
                    >
                      <DialogTrigger asChild>
                        <Button variant="outline" size="sm" className="mt-2">
                          <FileText className="h-4 w-4 mr-2" /> View Data
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Evaluation Data</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-2 text-sm">
                          {Object.entries(evaluation.evaluation_data).map(
                            ([key, value]) => (
                              <div key={key}>
                                <div className="font-medium">
                                  {formatLabel(key)}
                                </div>
                                {renderValue(value)}
                              </div>
                            )
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      <AlertDialog
        open={reevaluationTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReevaluationTarget(null);
            setSendCvFailureEmail(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{reevaluationConfig.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {reevaluationConfig.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {reevaluationTarget === "cv" && (
            <div className="flex items-center gap-2 py-2">
              <Checkbox
                id="cv-send-failure-email"
                checked={sendCvFailureEmail}
                onCheckedChange={(checked) =>
                  setSendCvFailureEmail(checked === true)
                }
              />
              <Label htmlFor="cv-send-failure-email" className="cursor-pointer">
                Send failure email to candidate if they don't pass
              </Label>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmReevaluation}
              disabled={reevaluationConfig.isPending}
            >
              {reevaluationConfig.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
